import { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import Modal from '../Modal/Modal.tsx'
import Button from '../../ui/Button/Button.tsx'
import IconResetLink from '../../../assets/icons/img-reset-link.svg?react'
import FormColumn from '@/components/ui/Form/components/FormColumn/FormColumn.tsx'
import { TextH2, TextNorm, TextSmall } from '../../ui/Text'
import { verifyEmailSaga, resendVerificationSaga, logoutSaga } from '../../../store/auth/authSlice.ts'
import IconClose from '../../../assets/icons/icon-close.svg?react'
import IconButton from '@/components/ui/IconButton/IconButton'
import { useAppSelector } from '../../../store/hooks.ts'
import { selectSignInError, selectIsLoading } from '../../../store/ui/uiSelectors.ts'
import { setSignInError } from '../../../store/ui/uiSlice.ts'
import OtpInput from './components/OtpInput/OtpInput.tsx'
import css from './VerifyEmailModal.module.css'

const RESEND_COOLDOWN = 60

interface VerifyEmailModalProps {
    email: string
}

const VerifyEmailModal = ({ email }: VerifyEmailModalProps) => {
    const dispatch = useDispatch()
    const error = useAppSelector(selectSignInError)
    const isVerifyLoading = useAppSelector(selectIsLoading('verify-email'))
    const [code, setCode] = useState('')
    const [resendCooldown, setResendCooldown] = useState(0)
    const [resendSent, setResendSent] = useState(false)

    useEffect(() => {
        if (resendCooldown <= 0) return
        const timer = setTimeout(() => setResendCooldown(c => c - 1), 1000)
        return () => clearTimeout(timer)
    }, [resendCooldown])

    const handleCodeChange = (val: string) => {
        setCode(val)
        if (error) dispatch(setSignInError(null))
    }

    const handleSubmit = () => {
        if (code.length < 6 || isVerifyLoading) return
        dispatch(verifyEmailSaga(code))
    }

    const handleResend = () => {
        if (resendCooldown > 0) return
        dispatch(resendVerificationSaga())
        setResendSent(true)
        setResendCooldown(RESEND_COOLDOWN)
        setCode('')
        if (error) dispatch(setSignInError(null))
    }

    const handleClose = () => {
        dispatch(logoutSaga())
    }

    return (
        <Modal onClose={handleClose} maxWidth={416} canClose={false}>
            <div className={css.buttons}>
                <IconButton icon={<IconClose />} onClick={handleClose} />
            </div>
            <div className={css.icon}>
                <IconResetLink />
            </div>
            <TextH2>Verify your email</TextH2>
            <div className={css.content}>
                <TextNorm>We've sent a 6-digit code to <strong>{email}</strong>. Please enter it below.</TextNorm>
            </div>
            <div className={css.otp}>
                <OtpInput
                    value={code}
                    onChange={handleCodeChange}
                    error={!!error}
                    disabled={isVerifyLoading}
                />
            </div>
            {error && (
                <div className={css.error}>
                    <TextSmall>{error}</TextSmall>
                </div>
            )}
            <FormColumn>
                <Button
                    title="Verify"
                    type="black"
                    size="medium"
                    onClick={handleSubmit}
                    loading={isVerifyLoading}
                    disabled={code.length < 6}
                />
            </FormColumn>
            <div className={css.resend}>
                <TextSmall>
                    Didn't receive a code?{' '}
                    {resendCooldown > 0 ? (
                        <span className={css.resend_sent}>Resend in {resendCooldown}s</span>
                    ) : (
                        <span className={css.resend_link} onClick={handleResend}>
                            {resendSent ? 'Resend again' : 'Resend code'}
                        </span>
                    )}
                </TextSmall>
            </div>
        </Modal>
    )
}

export default VerifyEmailModal
